import React from 'react';
import { cn } from '../../utils/cn';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'ghost' | 'glass' | 'gradient' | 'elevated' | 'interactive';
  hover?: boolean;
  animate?: boolean;
  glow?: boolean;
  blur?: boolean;
  children: React.ReactNode;
}

export function Card({ 
  className, 
  variant = 'default', 
  hover = false,
  animate = false,
  glow = false,
  blur = false,
  children,
  ...props 
}: CardProps) {
  const variants = {
    default: 'bg-card shadow-soft border border-border/50',
    bordered: 'bg-card border-2 border-border',
    ghost: 'bg-muted/50',
    glass: cn(
      'bg-white/5 dark:bg-gray-900/5',
      'backdrop-blur-xl backdrop-saturate-150',
      'border border-white/10 dark:border-white/5'
    ),
    gradient: cn(
      'bg-gradient-to-br from-white/80 to-white/40',
      'dark:from-surface-1/80 dark:to-surface-2/40',
      blur && 'backdrop-blur-sm',
      'border border-white/20 dark:border-white/10'
    ),
    elevated: cn(
      'bg-card',
      'shadow-xl dark:shadow-2xl',
      'shadow-gray-200/50 dark:shadow-black/20',
      'border border-border'
    ),
    interactive: cn(
      'bg-card cursor-pointer',
      'hover:shadow-xl hover:scale-[1.02]',
      'active:scale-[0.98]',
      'border border-border hover:border-primary/50',
      'group'
    )
  };
  
  return (
    <div
      className={cn(
        "relative rounded-xl transition-all duration-300 overflow-hidden",
        variants[variant],
        hover && "hover:shadow-lg hover:-translate-y-1",
        animate && "animate-fade-in",
        glow && "shadow-glow shadow-primary/20 hover:shadow-glow-lg hover:shadow-primary/30",
        className
      )}
      {...props}
    >
      {/* Gradient overlay for interactive variant */}
      {variant === 'interactive' && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-500 
                        pointer-events-none" />
      )}
      
      {children}
    </div>
  );
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardHeader({ className, children, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn("px-6 py-4 border-b border-border", className)}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

export function CardTitle({ className, children, ...props }: CardTitleProps) {
  return (
    <h3
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    >
      {children}
    </h3>
  );
}

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

export function CardDescription({ className, children, ...props }: CardDescriptionProps) {
  return (
    <p
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    >
      {children}
    </p>
  );
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardContent({ className, children, ...props }: CardContentProps) {
  return (
    <div
      className={cn("px-6 py-4", className)}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardFooter({ className, children, ...props }: CardFooterProps) {
  return (
    <div
      className={cn("px-6 py-4 border-t border-border", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export {
  type CardProps,
  type CardHeaderProps,
  type CardTitleProps,
  type CardDescriptionProps,
  type CardContentProps,
  type CardFooterProps
};
